import { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Title, BlogPostCard, StyledGrid, SwitchThemeContext } from '@tryhackme/thm-ui-components';
import { SEO } from 'src/common/components/seo';
import { useGetPostQuery, useGetPostsQuery } from 'src/features/api/ghost-slice';
import { CTABanner } from 'src/common/components';
import { ThemeColorMode } from 'src/common/enums';
import { ReactComponent as XIcon } from 'src/images/svgs/x-icon.svg';
import { ReactComponent as FacebookIcon } from 'src/images/svgs/facebook-icon.svg';
import { ReactComponent as LinkedInIcon } from 'src/images/svgs/linkedin-icon.svg';
import { getPdfUrls } from '../helpers';
import { ArticleLoading, StyledArticleCategoryLoading } from '../components/loading';
import { StyledBanner, StyledHeaderSectionWrapper, StyledSocialIconWrapper, StyledHeaderCategory, StyledArticleTitle, StyledPostWrapper, StyledAuthorWrapper, StyledAuthorSection, StyledRecommendedWrapper, StyledRecommendedSection, StyledSocialIcon } from './article.styles';
export const Article = () => {
  const navigate = useNavigate();
  const {
    slug
  } = useParams();
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  if (!slug) throw new Error('No slug or category in url params');
  const {
    data,
    isLoading,
    isError,
    error
  } = useGetPostQuery({
    slug
  });
  const {
    data: dataPosts,
    isFetching: postsLoading
  } = useGetPostsQuery({
    slug,
    limit: 4
  });
  const post = data?.posts[0];
  const recommendedArticles = dataPosts?.posts?.filter(anotherPost => anotherPost.slug !== slug);
  const articleCategory = post?.tags?.slice(-1).pop()?.name.toUpperCase();
  const articleData = (post?.html as string);
  const articleTitle = post?.title;
  const readingTime = post?.reading_time;
  const authorName = post?.authors?.[0]?.name;
  const profileImage = post?.authors?.[0]?.profile_image;
  const articlePublishedDate = post?.published_at;
  const publishedAt = new Date(String(articlePublishedDate)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const featureImage = post?.feature_image;
  const description = post?.excerpt;
  const socialIconLinks = [{
    id: 1,
    href: `https://twitter.com/intent/tweet?url=https://tryhackme.com/r/resources/blog/${slug}`,
    icon: XIcon,
    description: 'Share on X'
  }, {
    id: 2,
    href: `https://www.facebook.com/sharer/sharer.php?u=https://tryhackme.com/r/resources/blog/${slug}`,
    icon: FacebookIcon,
    description: 'Share on Facebook'
  }, {
    id: 3,
    href: `https://www.linkedin.com/sharing/share-offsite/?url=https://tryhackme.com/r/resources/blog/${slug}`,
    icon: LinkedInIcon,
    description: 'Share on LinkedIn'
  }];
  useEffect(() => {
    if (isError && error && 'status' in error && error?.status === 404) {
      navigate('/404');
    }
  }, [navigate, isError, error]);
  return <>
      <section>
        <SEO title={articleTitle ?? 'Resources'} description={description ?? 'TryHackMe is a free online platform for learning cyber security, using hands-on exercises and labs, all through your browser!'} data-sentry-element="SEO" data-sentry-source-file="article.tsx" />
        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
        <StyledBanner data-sentry-element="StyledBanner" data-sentry-source-file="article.tsx">{featureImage ? <img src={featureImage} alt="Feature" /> : <></>}</StyledBanner>
        <Container data-sentry-element="Container" data-sentry-source-file="article.tsx">
          <StyledHeaderSectionWrapper data-sentry-element="StyledHeaderSectionWrapper" data-sentry-source-file="article.tsx">
            <StyledHeaderCategory data-sentry-element="StyledHeaderCategory" data-sentry-source-file="article.tsx">
              {isLoading ? <StyledArticleCategoryLoading /> : <>
                  {articleCategory} • {readingTime} min read{' '}
                </>}
            </StyledHeaderCategory>
            <StyledSocialIconWrapper data-sentry-element="StyledSocialIconWrapper" data-sentry-source-file="article.tsx">
              {socialIconLinks.map(link => <StyledSocialIcon key={link.id} href={link.href} target="_blank" rel="noreferrer" $isDark={isDarkMode}>
                  <link.icon />
                </StyledSocialIcon>)}
            </StyledSocialIconWrapper>
          </StyledHeaderSectionWrapper>
          <StyledArticleTitle data-sentry-element="StyledArticleTitle" data-sentry-source-file="article.tsx">
            <Title titleText={articleTitle} isLeftAlign isLoading={isLoading} data-sentry-element="Title" data-sentry-source-file="article.tsx" />
          </StyledArticleTitle>

          {isError && <StyledPostWrapper $isDarkMode={isDarkMode}>
              <p>Oops! Something went wrong.</p>
            </StyledPostWrapper>}

          {isLoading ? <StyledPostWrapper $isDarkMode={isDarkMode}>
              <ArticleLoading />
            </StyledPostWrapper> : <StyledPostWrapper $isDarkMode={isDarkMode}>
              {/* eslint-disable-next-line react/no-danger -- this comes from CMS */}
              <div dangerouslySetInnerHTML={{
            __html: articleData
          }} />
            </StyledPostWrapper>}
          {!isLoading && <StyledAuthorWrapper>
              <StyledAuthorSection>
                <>
                  <img src={profileImage} alt="author" />
                  {authorName}
                  <br />
                  {publishedAt}
                </>
              </StyledAuthorSection>
              <StyledSocialIconWrapper>
                {socialIconLinks.map(link => <StyledSocialIcon key={link.id} href={link.href} target="_blank" rel="noreferrer" aria-label={link.description} $isDark={isDarkMode}>
                    <link.icon />
                  </StyledSocialIcon>)}
              </StyledSocialIconWrapper>
            </StyledAuthorWrapper>}
        </Container>

        {!postsLoading && <StyledRecommendedWrapper>
            <Title titleText="Recommended" subTitleText="Get more insights, news, and assorted awesomeness around cyber training." isTitleSmall />

            <StyledRecommendedSection>
              <StyledGrid gridTemplateColumns="col-3" gap="4.1rem 2rem">
                {recommendedArticles?.slice(0, 3).map(article => {
              const postUrl = article?.primary_tag?.slug === 'business-resource' ? `${getPdfUrls(article)}` : `/resources/blog/${article.slug}`;
              const lastTag = article?.tags?.slice(-1).pop()?.name === 'business-resource' ? 'Business' : article?.tags?.slice(-1).pop()?.name;
              return <BlogPostCard key={article.slug} articleTitle={article.title} imageProps={{
                src: article.feature_image ?? '',
                alt: article?.slug
              }} readingTime={`${article.reading_time} min read`} extract={article.custom_excerpt} category={lastTag} href={postUrl} target="_blank" />;
            })}
              </StyledGrid>
            </StyledRecommendedSection>
          </StyledRecommendedWrapper>}
      </section>
      <CTABanner data-sentry-element="CTABanner" data-sentry-source-file="article.tsx" />
    </>;
};